import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useTechnologyProductFamilies } from '../../graphql/queries';
import {
  updateTechnologyProductFamily,
  deleteTechnologyProductFamily,
  ApiError,
} from '../../lib/apiClient';
import { LoadingSpinner, EmptyState, RichTextEditor, isDeltaEmpty } from '../../components/shared';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';

export const Route = createFileRoute('/technology-families/$familyId/edit')({
  component: EditTechnologyProductFamilyPage,
});

export { EditTechnologyProductFamilyPage };

function EditTechnologyProductFamilyPage(): JSX.Element {
  const { familyId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data, loading, error: fetchError } = useTechnologyProductFamilies();
  const family = data?.technologyProductFamilies?.find((f) => f.entityId === familyId);

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (family) {
      setName(family.name);
      setDescription(family.description ?? '');
    }
  }, [family]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await updateTechnologyProductFamily(familyId, {
        name: name.trim(),
        description: isDeltaEmpty(description) ? undefined : description,
      });
      await apolloClient.refetchQueries({ include: ['ListTechnologyProductFamilies'] });
      void navigate({ to: '/technology-families' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update product family');
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!window.confirm(`Delete "${family?.name}"? This cannot be undone.`)) return;
    setIsSubmitting(true);
    try {
      await deleteTechnologyProductFamily(familyId);
      await apolloClient.refetchQueries({ include: ['ListTechnologyProductFamilies'] });
      void navigate({ to: '/technology-families' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete product family');
      setIsSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner centered message="Loading product family..." />;

  if (fetchError || !family) {
    return (
      <EmptyState
        icon="error"
        title="Product family not found"
        description={fetchError?.message ?? 'Family not found'}
        actionLabel="Back to Families"
        onAction={() => void navigate({ to: '/technology-families' })}
      />
    );
  }

  return (
    <div className="simple-entity-form-page">
      <div className="simple-entity-form-page__back-link">
        <Link to="/technology-families" className="simple-entity-form-page__back-link-inner">
          <IconElement
            name="arrow_back"
            aria-hidden="true"
            className="simple-entity-form-page__back-icon"
          />
          <span className="forge-typography--body2">Back to Product Families</span>
        </Link>
      </div>

      <h1 className="forge-typography--headline4 simple-entity-form-page__title">
        Edit Product Family
      </h1>

      {error && (
        <p role="alert" className="simple-entity-form-page__error">
          {error}
        </p>
      )}

      <form onSubmit={handleSubmit} className="simple-entity-form-page__form">
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Name <span className="simple-entity-form-page__required">*</span>
          </label>
          <input
            className="forge-text-field__input simple-entity-form-page__control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="forge-typography--body2 simple-entity-form-page__label">
            Description
          </label>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="Brief description of this product family"
          />
        </div>
        <div className="simple-entity-form-page__actions simple-entity-form-page__actions--split">
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={() => void handleDelete()}
            disabled={isSubmitting}
            className="simple-entity-form-page__delete-button"
          >
            Delete Family
          </ForgeButton>
          <div className="simple-entity-form-page__actions-group">
            <Link to="/technology-families">
              <ForgeButton type="button" variant="outlined" disabled={isSubmitting}>
                Cancel
              </ForgeButton>
            </Link>
            <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
              Save Changes
            </ForgeButton>
          </div>
        </div>
      </form>
    </div>
  );
}
